import { useEffect, useState } from 'react';

import { Tab, TabList, TabPanel, TabPanels, Tabs } from '@reach/tabs';
import '@reach/tabs/styles.css';
import { useAuthorization } from 'modules/auth';
import { StateCheck, getCurrentInterviewFetched } from 'modules/data';
import { useI18n } from 'modules/i18n';
import { usePathBase, useProject } from 'modules/routes';
import { Spinner } from 'modules/spinners';
import { InlineNotification } from 'modules/ui';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';

import * as indexes from '../constants';
import tabIndexFromRoute from '../tabIndexFromRoute';
import AdminTabPanel from './AdminTabPanel';
import ArchiveSearchTabPanel from './ArchiveSearchTabPanel';
import IndexingTabPanel from './IndexingTabPanel';
import InterviewTabPanelContainer from './InterviewTabPanelContainer';
import MapTabPanelContainer from './MapTabPanelContainer';
import ProjectConfigTabPanel from './ProjectConfigTabPanel';
import RegistryEntriesTabPanelContainer from './RegistryEntriesTabPanelContainer';
import WorkbookTabPanel from './WorkbookTabPanel';

/**
 * Shows route tabs according to project settings, session state, and permissions.
 *
 * @param {Object} props - Sidebar context.
 * @param {Array} [props.selectedArchiveIds] - Selected public interview identifiers.
 * @param {Object} [props.interview] - Current interview, when available.
 * @param {string} [props.archiveId] - Current public interview identifier.
 * @param {boolean} [props.isLoggedIn] - Whether a user is signed in.
 * @returns {React.ReactElement} Sidebar tabs or project request status.
 */
export default function SidebarTabs({
    selectedArchiveIds,
    interview,
    archiveId,
    isLoggedIn,
}) {
    const { t, locale } = useI18n();
    const { project, isLoading, error } = useProject();
    const { isAuthorized } = useAuthorization();
    const pathBase = usePathBase();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const isInterviewRoute = pathname.includes('/interviews/');

    const hasMap = project?.has_map;
    const isCampscapesProject = project?.shortname === 'campscapes';
    const projectName =
        project?.name || project?.display_name || project?.shortname || '';

    const [tabIndex, setTabIndex] = useState(() =>
        tabIndexFromRoute(pathBase, pathname, isCampscapesProject)
    );

    useEffect(() => {
        setTabIndex(tabIndexFromRoute(pathBase, pathname, isCampscapesProject));
    }, [pathname, pathBase, isCampscapesProject]);

    function handleTabClick(index) {
        setTabIndex(index);

        switch (index) {
            case indexes.INDEX_SEARCH:
                navigate(`${pathBase}/searches/archive`);
                break;
            case indexes.INDEX_CATALOG:
                navigate(`${pathBase}/catalog`);
                break;
            case indexes.INDEX_CATALOG_INSTITUTIONS:
                navigate(`${pathBase}/catalog/institutions`);
                break;
            case indexes.INDEX_INTERVIEW:
                navigate(`${pathBase}/interviews/${archiveId}`);
                break;
            case indexes.INDEX_REGISTRY_ENTRIES:
                navigate(`${pathBase}/registry_entries`);
                break;
            case indexes.INDEX_MAP:
                navigate(`${pathBase}/searches/map`);
                break;
            case indexes.INDEX_PROJECTS:
                navigate(`/${locale}/projects`);
                break;
            case indexes.INDEX_INSTITUTIONS:
                navigate(`/${locale}/institutions`);
                break;
            case indexes.INDEX_HELP_TEXTS:
                navigate(`/${locale}/help_texts`);
                break;
            default:
        }
    }

    const showCatalogTab = project?.is_umbrella;
    const showInterviewTab = !!interview;
    const showRegistryTab =
        (!isLoggedIn && project?.has_visible_registry_entries) || isLoggedIn;
    const showMapTab = hasMap && !project?.is_umbrella;
    const showWorkbookTab = isLoggedIn;
    const showIndexingTab = isAuthorized({ type: 'General' }, 'edit');
    const showAdministrationTab = isAuthorized({ type: 'General' }, 'edit');
    const showProjectAdminTab = isAuthorized({ type: 'Project' }, 'update');
    const showProjectsTab =
        project?.is_umbrella && isAuthorized({ type: 'Project' }, 'create');
    const showInstitutionsTab =
        project?.is_umbrella && isAuthorized({ type: 'Institution' }, 'create');
    const showHelpTextsTab =
        project?.is_umbrella && isAuthorized({ type: 'HelpText' }, 'update');

    // TODO: Remove this special handling
    const isCampscapesWithoutArchive = isCampscapesProject && !archiveId;
    const searchTabLabelKey = isCampscapesWithoutArchive
        ? 'user.notes_on_tos_agreement'
        : project?.is_umbrella
          ? 'modules.sidebar.search'
          : 'archive_search';
    const searchTabLabelParams = isCampscapesWithoutArchive
        ? { project: projectName }
        : undefined;

    if (error) {
        return (
            <InlineNotification
                variant="error"
                title={t('error')}
                description={error.message}
                isClosable={false}
            />
        );
    }
    if (isLoading || !project) return <Spinner />;

    return (
        <Tabs
            className="SidebarTabs"
            orientation="vertical"
            keyboardActivation="manual"
            index={tabIndex}
            onChange={handleTabClick}
        >
            <TabList>
                <Tab
                    key="1"
                    className="SidebarTabs-tab"
                    disabled={!showCatalogTab}
                >
                    {t('modules.sidebar.catalog')}
                </Tab>

                <Tab
                    key="2"
                    className="SidebarTabs-tab"
                    disabled={!showCatalogTab}
                >
                    {t('activerecord.models.institution.other')}
                </Tab>

                <Tab key="3" className="SidebarTabs-tab">
                    {t(searchTabLabelKey, searchTabLabelParams)}
                </Tab>

                <Tab
                    key="4"
                    className="SidebarTabs-tab"
                    disabled={!showInterviewTab}
                >
                    {t('interview')}
                </Tab>

                <Tab
                    key="5"
                    className="SidebarTabs-tab"
                    disabled={!showRegistryTab}
                >
                    {t('registry')}
                </Tab>

                <Tab key="6" className="SidebarTabs-tab" disabled={!showMapTab}>
                    {t('map')}
                </Tab>

                <Tab
                    key="7"
                    className="SidebarTabs-tab"
                    disabled={!showWorkbookTab}
                >
                    {t('user_content')}
                </Tab>

                <Tab
                    key="8"
                    className="SidebarTabs-tab SidebarTabs-tab--admin"
                    disabled={!showIndexingTab}
                >
                    {t('edit.indexing')}
                </Tab>

                <Tab
                    key="9"
                    className="SidebarTabs-tab SidebarTabs-tab--admin"
                    disabled={!showAdministrationTab}
                >
                    {t('edit.administration')}
                </Tab>

                <Tab
                    key="10"
                    className="SidebarTabs-tab SidebarTabs-tab--admin"
                    disabled={!showProjectAdminTab}
                >
                    {t('edit.project.admin')}
                </Tab>

                <Tab
                    key="11"
                    className="SidebarTabs-tab SidebarTabs-tab--admin"
                    disabled={!showProjectsTab}
                >
                    {t('edit.projects.admin')}
                </Tab>

                <Tab
                    key="12"
                    className="SidebarTabs-tab SidebarTabs-tab--admin"
                    disabled={!showInstitutionsTab}
                >
                    {t('edit.institution.admin')}
                </Tab>

                <Tab
                    key="13"
                    className="SidebarTabs-tab SidebarTabs-tab--admin"
                    disabled={!showHelpTextsTab}
                >
                    {t('activerecord.models.help_text.other')}
                </Tab>
            </TabList>

            <TabPanels>
                <TabPanel key="1" />

                <TabPanel key="2" />

                <TabPanel key="3">
                    {tabIndex === indexes.INDEX_SEARCH && (
                        <ArchiveSearchTabPanel
                            selectedArchiveIds={selectedArchiveIds}
                        />
                    )}
                </TabPanel>

                <TabPanel key="4">
                    {showInterviewTab &&
                        isInterviewRoute &&
                        tabIndex === indexes.INDEX_INTERVIEW && (
                            <StateCheck
                                testSelector={getCurrentInterviewFetched}
                                fallback={<Spinner withPadding />}
                            >
                                <InterviewTabPanelContainer />
                            </StateCheck>
                        )}
                </TabPanel>

                <TabPanel key="5">
                    {showRegistryTab &&
                        tabIndex === indexes.INDEX_REGISTRY_ENTRIES && (
                            <RegistryEntriesTabPanelContainer />
                        )}
                </TabPanel>

                <TabPanel key="6">
                    {showMapTab && tabIndex === indexes.INDEX_MAP && (
                        <MapTabPanelContainer />
                    )}
                </TabPanel>

                <TabPanel key="7">
                    {showWorkbookTab && tabIndex === indexes.INDEX_WORKBOOK && (
                        <WorkbookTabPanel />
                    )}
                </TabPanel>

                <TabPanel key="8">
                    {showIndexingTab && tabIndex === indexes.INDEX_INDEXING && (
                        <IndexingTabPanel />
                    )}
                </TabPanel>

                <TabPanel key="9">
                    {showAdministrationTab &&
                        tabIndex === indexes.INDEX_ADMINISTRATION && (
                            <AdminTabPanel />
                        )}
                </TabPanel>

                <TabPanel key="10">
                    {showProjectAdminTab &&
                        tabIndex === indexes.INDEX_PROJECT_ACCESS && (
                            <ProjectConfigTabPanel />
                        )}
                </TabPanel>

                <TabPanel key="11" />

                <TabPanel key="12" />

                <TabPanel key="13" />
            </TabPanels>
        </Tabs>
    );
}

SidebarTabs.propTypes = {
    interview: PropTypes.object,
    archiveId: PropTypes.string,
    isLoggedIn: PropTypes.bool,
    selectedArchiveIds: PropTypes.array,
};
